import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Avatar, AvatarGroup, Box, ListItemButton, Tooltip, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { grey } from "../../../theme/muiColors";
import { avatarColorOptions } from "../../ui/colors";
import { useJobsByIdQuery } from "../../jobs/jobs-list/jobs-layout/hooks/useJobByIdQuery";

export interface JobChatSummary {
  jobEventUid: string;
  jobId: number;
  companyId: number;
  alias: string | null;
  status: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  event?: {
    uid: string;
    status: string;
    startDate: string;
    endDate: string;
    eventUid: string | null;
    jobId: number;
    jobStatus: string | null;
    alias: string | null;
    companyId: number;
  };
  workforce?: {
    uid: string;
    firstName: string;
    lastName: string;
    avatar: string | null;
    role: string | null;
  }[];
  coworkers?: {
    uid: string;
    firstName: string;
    lastName: string;
    avatar: string | null;
    role: string | null;
    roleName: string | null;
  }[];
  lastMessage: string | null;
  lastMessageAt: string | null;
  firstMessageAt?: string | null;
  chatStartedAt?: string | null;
  closedAt?: string | null;
  createdAt?: string | null;
  serviceName?: string | null;
  service?: string | { name?: string | null } | null;
  jobType?: string | null;
  type?: string | null;
  scopeOfWork?: any;
  workScope?: any;
  job?: any;
  messagesCount: number;
  unreadCount?: number;
  lastReadAt?: string | null;
  client?: {
    firstName: string;
    lastName: string;
  };
}

const formatConversationDate = (date?: string | null) => {
  if (!date) return "";
  const value = new Date(date);
  const now = new Date();
  const isToday = value.toDateString() === now.toDateString();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = value.toDateString() === yesterday.toDateString();

  if (isToday) return value.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isYesterday) return "Yesterday";
  return value.toLocaleDateString([], { month: "short", day: "numeric" });
};

const formatEventDate = (date?: string | null) => {
  if (!date) return "";

  const value = new Date(date);
  if (!Number.isFinite(value.getTime())) return "";

  return value.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatElapsedTime = (startedAt?: string | null, endedAt?: string | null, currentTime = Date.now()) => {
  if (!startedAt) return "";

  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : currentTime;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "";

  const totalMinutes = Math.max(0, Math.floor((end - start) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getAvatarColor = (name: string) => {
  const value = name.split("").reduce((total, character) => total + character.charCodeAt(0), 0);
  return avatarColorOptions[value % avatarColorOptions.length];
};

const getServiceName = (chat: JobChatSummary) => {
  const job = chat.job;
  const service = chat.service ?? job?.quote?.services?.[0]?.service;
  const categoryName =
    chat.scopeOfWork?.category?.name ||
    chat.workScope?.category?.name ||
    job?.quote?.services?.[0]?.scopeOfWork?.category?.name;
  const isJobType = ["residential", "residencial", "commercial", "comercial"].includes(categoryName?.toLowerCase());

  return (
    chat.serviceName ||
    (typeof service === "string" ? service : service?.name) ||
    job?.scopeOfWork?.typeOfService?.name ||
    job?.workScope?.quotes?.[0]?.workScopeServices?.[0]?.workService?.name ||
    job?.quote?.workScopeServices?.[0]?.workService?.name ||
    (!isJobType ? categoryName : null) ||
    null
  );
};

const getJobType = (chat: JobChatSummary) => {
  const job = chat.job;
  const candidates = [
    chat.jobType,
    chat.type,
    job?.type,
    job?.client?.type,
    job?.quote?.client?.type,
    chat.scopeOfWork?.category?.type,
    chat.scopeOfWork?.category?.name,
    chat.workScope?.workType?.type,
    chat.workScope?.category?.type,
    chat.workScope?.category?.name,
    job?.quote?.services?.[0]?.scopeOfWork?.category?.type,
    job?.quote?.services?.[0]?.scopeOfWork?.category?.name,
  ];

  for (const candidate of candidates) {
    const normalized = typeof candidate === "string" ? candidate.toLowerCase() : "";
    if (normalized.includes("residential") || normalized.includes("residencial")) return "Residential";
    if (normalized.includes("commercial") || normalized.includes("comercial")) return "Commercial";
  }

  return null;
};

interface JobChatListItemProps {
  chat: JobChatSummary;
  isSelected: boolean;
  currentTime?: number;
  onSelect: (chat: JobChatSummary) => void;
  isEmployee?: boolean;
  companyDisplayName?: string;
}

export function JobChatListItem({
  chat,
  isSelected,
  currentTime = Date.now(),
  onSelect,
  isEmployee = false,
  companyDisplayName = "Company",
}: JobChatListItemProps) {
  const { data: fetchedJob } = useJobsByIdQuery({ idStr: chat.jobId });
  const chatWithJob = chat.job ? chat : { ...chat, job: fetchedJob };
  const jobAlias = chat.alias || chat.event?.alias || chatWithJob.job?.alias;
  const serviceName = getServiceName(chatWithJob);
  const jobType = getJobType(chatWithJob);
  const eventDate = formatEventDate(chat.event?.startDate || chat.startDate);
  const jobTitle = [eventDate, `Job #${chat.jobId}`, jobAlias]
    .filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" • ");
  const jobDetails = [serviceName, jobType]
    .filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(" • ");
  const lastMessageDate = formatConversationDate(chat.lastMessageAt);
  const conversationStartedAt = chat.firstMessageAt || chat.chatStartedAt || chat.createdAt || chat.startDate;
  const conversationEndedAt = chat.isActive ? null : chat.closedAt || chat.lastMessageAt || chat.endDate;
  const elapsedTime = formatElapsedTime(conversationStartedAt, conversationEndedAt, currentTime);

  const rawParticipants = [
    ...(chat.workforce || []),
    // Coworkers can access job chats implicitly, but are hidden from visible member avatars.
    // ...(chat.coworkers || []),
  ];
  const participants = isEmployee
    ? []
    : Array.from(new Map(rawParticipants.map((p) => [p.uid, p])).values());

  const formatParticipant = (participant: (typeof participants)[number]) => {
    const name = [participant.firstName, participant.lastName].filter(Boolean).join(" ");
    const rawRole = "roleName" in participant ? participant.roleName || participant.role : participant.role;
    const role = rawRole?.toLowerCase() === "business" ? "Owner" : rawRole;
    return role ? `${name} (${role})` : name;
  };

  const participantTooltip = (
    <Box>
      {chat.workforce?.length ? (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Workers</Typography>
          {chat.workforce.map((participant) => (
            <Typography key={participant.uid} sx={{ fontSize: 11 }}>{formatParticipant(participant)}</Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );

  const unreadCount = chat.unreadCount ?? 0;
  const hasUnread = unreadCount > 0 && !isSelected;
  return (
    <ListItemButton
      onClick={() => onSelect(chat)}
      sx={{
        py: 1.15,
        px: 1.5,
        mb: 0.6,
        borderRadius: "14px",
        backgroundColor: isSelected ? orange[50] : hasUnread ? orange[50] : "transparent",
        transition: "all 0.2s",
        display: "block",
        "&:hover": { bgcolor: isSelected ? orange[50] : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.035)" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={jobTitle} sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
          {jobTitle}
        </Typography>
        {jobDetails && (
          <Typography
            noWrap
            title={jobDetails}
            sx={{
              mt: 0.25,
              color: grey[500],
              fontSize: "11px",
              fontWeight: 400,
              lineHeight: 1.25,
            }}
          >
            {jobDetails}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 0.85, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", alignItems: "center", gap: 1 }}>
        {participants.length > 0 ? (
          <Tooltip
            title={participantTooltip}
            placement="top"
            arrow
            enterDelay={100}
            slotProps={{
              popper: { sx: { zIndex: "2147483647 !important" } },
              tooltip: { sx: { maxWidth: 200 } },
            }}
          >
            <AvatarGroup
              max={4}
              sx={{
                justifyContent: "flex-start",
                width: "fit-content",
                "& .MuiAvatar-root": { width: 22, height: 22, fontSize: "9px", border: "1px solid var(--fx-surface-base, #FFFFFF)" },
              }}
            >
              {participants.map((participant) => {
                const name = [participant.firstName, participant.lastName].filter(Boolean).join(" ");
                return (
                  <Avatar
                    key={participant.uid}
                    src={participant.avatar || undefined}
                    alt={name}
                    sx={{ bgcolor: participant.avatar ? undefined : getAvatarColor(name) }}
                  >
                    {name[0]?.toUpperCase() || "?"}
                  </Avatar>
                );
              })}
            </AvatarGroup>
          </Tooltip>
        ) : <Box />}

        <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.7 }}>
          <Typography
            noWrap
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "11px",
              color: hasUnread ? "text.primary" : "text.secondary",
              fontWeight: hasUnread ? 600 : 400,
              lineHeight: 1.35,
            }}
          >
            {chat.lastMessage || "No messages yet"}
          </Typography>
          {lastMessageDate && (
            <Typography sx={{ flexShrink: 0, fontSize: "9px", color: grey[500], whiteSpace: "nowrap" }}>
              {lastMessageDate}
            </Typography>
          )}
        </Box>

        {elapsedTime && (
          <Tooltip title="Conversation time" arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, color: orange[700], whiteSpace: "nowrap" }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 11 }} />
              <Typography component="span" sx={{ fontSize: "9px", fontWeight: 500, color: "inherit" }}>
                {elapsedTime}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    </ListItemButton>
  );
}
